import React, { FC, memo, useState } from 'react';

import { useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { ActionButtonVariants, ActionLink } from 'components/action-button';
import PointsIcon from 'components/icons/loyalty-points-icon';
import { centeredRowContainerStyles } from 'components/layout/centered-row-container';
import { PullToReloadScrollView } from 'components/pull-to-reload/pull-to-reload';
import { QuestSection } from 'components/quests';
import { useLoyaltyQuestQuery } from 'components/quests/loyalty-quest/use-loyalty-quest-query';
import { LoyaltyCmsType } from 'generated/graphql-gateway';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { routes } from 'navigation/routing';
import { AccountLink } from 'pages/account/account-link';
import { AccountIcon } from 'pages/account/icons/account-icon';
import { CommunicationPreferencesIcon } from 'pages/account/icons/communication-preferences-icon';
import { PartnershipsIcon } from 'pages/account/icons/partnerships-icon';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { AllowedCelebrationTypes } from 'state/loyalty/hooks/types';
import { useLoyaltyCelebrations } from 'state/loyalty/hooks/use-loyalty-celebrations';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useLoyaltyContext } from 'state/loyalty/loyalty-context';
import { primitive } from 'styles/constants/primitives';
import { WEB_EXTRA_SMALL_MAX_WIDTH, WEB_SMALL_MAX_WIDTH } from 'utils/constants';
import { isMobile } from 'utils/environment';
import { waitForDuration } from 'utils/wait-for-duration';

import { useGetLoyaltyBadges } from '../loyalty-badges/use-get-loyalty-badges';
import { LoyaltyNotEnrolled } from '../loyalty-not-enrolled';

import { BadgesSection } from './badges-section/badges-section';
import { ProfileCard } from './profile-card/profile-card';
import { UnauthenticatedProfile } from './unauthenticated-profile/unauthenticated-profile';
import { UserJourneyChallenge } from './user-journey-challenge/user-journey-challenge';

/**
 *
 * LoyaltyProfile displays the user's loyalty profile
 *
 */
export const LoyaltyProfile: FC = memo(() => {
  const { formatMessage } = useIntl();
  const [isForceRefreshing, setForceRefresh] = useState<boolean>(false);
  const { user, loading: loadingUser, isAuthenticated } = useAuthContext();
  const { loyaltyUser } = useLoyaltyUser();
  const { refetchLoyaltyUser } = useLoyaltyContext();
  const {
    loading: rewardDataIsLoading,
    engineRewardsMap,
    refetchRewards,
  } = useLoyaltyRewardsList();
  const enabledBadges = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_BADGES);
  const enableWalmartLink = useFlag(LaunchDarklyFlag.ENABLE_WALMART_PLUS_INTEGRATION);
  const enableLoyaltyCelebrations = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_CELEBRATIONS);
  const enableUserJourneyChallenge = useFlag(LaunchDarklyFlag.ENABLE_USER_JOURNEY_CHALLENGE);
  const { loading: badgesLoading, badges, refetch: refetchBadges } = useGetLoyaltyBadges({
    skip: !enabledBadges,
  });

  const { recheckCelebrations } = useLoyaltyCelebrations({
    allowedCelebrationTypes: [
      AllowedCelebrationTypes.PROFILE_CHALLENGE_COMPLETED,
      AllowedCelebrationTypes.BADGE_EARNED,
      AllowedCelebrationTypes.PARTIAL_PROFILE_CHALLENGE_COMPLETED,
    ],
    disableCelebrations: !enableLoyaltyCelebrations,
    onBadgeCelebration: refetchBadges,
  });

  const loyaltyId = user?.loyaltyId;

  const {
    loadingQuests: userJourneyChallengeLoading,
    refetchQuests: refetchUserJourneyChallenge,
    quest: userJourneyChallenge,
  } = useLoyaltyQuestQuery({
    loyaltyId,
    cmsType: LoyaltyCmsType.USER_JOURNEY,
    refecthOnFocus: true,
  });

  const forceRefresh = async () => {
    // We need this delay in order to avoid cutting the spinning animation
    await waitForDuration(1500);
    if (loyaltyId) {
      setForceRefresh(true);
      await Promise.allSettled([
        refetchLoyaltyUser(),
        refetchRewards(loyaltyId),
        refetchBadges(),
        refetchUserJourneyChallenge(),
      ]);
      setForceRefresh(false);
      recheckCelebrations();
    }
  };

  if (isAuthenticated && !loyaltyId && !loadingUser) {
    return <LoyaltyNotEnrolled />;
  }

  const isLoading = loadingUser || isForceRefreshing;

  return (
    <PullToReloadScrollView
      progressViewOffset={10}
      onRefresh={forceRefresh}
      contentContainerStyle={centeredRowContainerStyles.container}
      isDisabled={!isAuthenticated}
    >
      {!isAuthenticated ? (
        <UnauthenticatedProfile />
      ) : (
        <View style={styles.pageContainer}>
          <View style={styles.profileCardContainer}>
            <View style={[styles.wrapper, styles.xsWrapper]}>
              <ProfileCard
                loyaltyUser={loyaltyUser}
                engineRewardsMap={engineRewardsMap}
                loading={rewardDataIsLoading || isLoading}
              />
            </View>
          </View>

          {enableUserJourneyChallenge && (
            <UserJourneyChallenge
              loading={userJourneyChallengeLoading && !userJourneyChallenge}
              challenge={userJourneyChallenge}
            />
          )}

          <View style={[styles.wrapper, styles.questSectionContainer]}>
            <QuestSection showSmallHeader />
          </View>

          {enabledBadges && (
            <BadgesSection badges={badges} loading={badgesLoading || isForceRefreshing} />
          )}

          <View style={styles.wrapper}>
            <View style={styles.separator} />

            <ActionLink
              to={routes.claimPointsBase}
              startIcon={
                <PointsIcon fill={primitive.bk.$bbqBrown} style={{ width: 20, height: 20 }} />
              }
              variant={ActionButtonVariants.OUTLINE}
              style={styles.claimPointsLink}
            >
              {formatMessage({
                id: 'enterReceiptCode',
                defaultMessage: 'Enter Receipt Code to Earn',
              })}
            </ActionLink>

            <AccountLink
              link={routes.accountInfo}
              label={formatMessage({ id: 'accountInfo', defaultMessage: 'Account Info' })}
              leftIcon={<AccountIcon />}
            />
            <AccountLink
              link={routes.communication}
              label={formatMessage({
                id: 'communicationPreferences',
                defaultMessage: 'Communication Preferences',
              })}
              leftIcon={<CommunicationPreferencesIcon />}
            />

            {enableWalmartLink && (
              <AccountLink
                link={routes.partnerships}
                label={formatMessage({ id: 'partnerships', defaultMessage: 'Partnerships' })}
                leftIcon={<PartnershipsIcon />}
              />
            )}
          </View>
        </View>
      )}
    </PullToReloadScrollView>
  );
});

const styles = StyleSheet.create({
  profileCardContainer: {
    width: '100%',
    display: 'flex',
    paddingTop: isMobile() ? primitive.spacing.$7 : primitive.spacing.$9,
    paddingBottom: isMobile() ? primitive.spacing.$6 : primitive.spacing.$7,
    backgroundColor: primitive.bk.$lightMustard,
  },
  wrapper: {
    ...centeredRowContainerStyles.content,
    paddingHorizontal: primitive.spacing.$4,
    maxWidth: WEB_SMALL_MAX_WIDTH,
  },
  xsWrapper: {
    ...(!isMobile() && {
      maxWidth: WEB_EXTRA_SMALL_MAX_WIDTH,
      boxSizing: 'content-box',
    }),
  },
  pageContainer: {
    width: '100%',
    alignItems: 'center',
    gap: 24,
    paddingBottom: 34,
  },
  claimPointsLink: {
    width: '100%',
    maxWidth: WEB_SMALL_MAX_WIDTH,
    paddingHorizontal: primitive.spacing.$4,
    marginBottom: primitive.spacing.$6,
  },
  titleText: {
    fontSize: primitive.fontSizes.lg,
    fontFamily: primitive.fontFamily.$headers,
    alignItems: 'flex-end',
    display: 'flex',
  },
  separator: {
    borderTopWidth: 1,
    marginTop: primitive.spacing.$4,
    marginBottom: primitive.spacing.$6,
  },
  questSectionContainer: {
    width: '100%',
    ...(!isMobile() && {
      maxWidth: WEB_EXTRA_SMALL_MAX_WIDTH,
      boxSizing: 'content-box',
    }),
  },
});
